import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { InternalMeetingNoteType } from '../../models/AdminModels';
import { useUserSession } from '../../models/hooks/appStateHooks';

export interface IUpdateAccountNoteRequest {
	/** The account the note hangs off of. */
	accountId: string;
	/** Which internal note slot is being written (`Sales` / `Summary` / `CSM`). */
	type: InternalMeetingNoteType;
	/** The existing note's id, if any — an existing Sales note is edited in place via `note/{id}`. */
	noteId?: string;
	/** The note body as a raw rich-text content state (produced from the editor's HTML). */
	content: Api.IRawRichTextContentState;
	/** Referenced entities (e.g. CC'd employees on `users`); the server emails referenced users. */
	referencedEntities?: Api.IRichContentReferencedEntities;
	/** Action items attached inline on the note (same as the meeting note); omit for none. */
	actionItems?: Api.IActionItem[];
}

/**
 * Saves an Account Overview note via `PUT admin/account/{id}/additionalInfo/note/{type}` (the same
 * endpoint the legacy MobX `AccountOperationsViewModel.updateAccountNote` hit) and returns the saved
 * `Api.INote`. The body is just `{ content }`; the backend resolves the note slot from the path. The
 * caller invalidates the note + account queries on success. Mutations have no MutationCache, so the
 * error toast is wired here explicitly.
 */
export function useUpdateAccountNoteMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ accountId, type, noteId, content, referencedEntities, actionItems }: IUpdateAccountNoteRequest) => {
			// Match the legacy NoteEditor payload: notes are always public here and always carry the
			// three referenced-entity arrays (the `users` array is how CC'd employees are conveyed).
			// Action items ride inline on the note (same as the meeting note), when present.
			const note: Api.INote = {
				content,
				visibility: 'all',
				referencedEntities: referencedEntities ?? { companies: [], contacts: [], users: [] },
				...(actionItems?.length ? { actionItems } : {}),
			};

			// Legacy parity: an existing Sales note is edited in place via `note/{id}`; every other
			// case (Summary / CSM / a brand-new Sales note) upserts the account's per-type note slot.
			if (type === InternalMeetingNoteType.Sales && noteId) {
				return userSession.webServiceHelper.callAsync<Api.INote>(`note/${noteId}`, 'PUT', { ...note, id: noteId });
			}

			return userSession.webServiceHelper.callAsync<Api.INote>(
				`admin/account/${accountId}/additionalInfo/note/${type}`,
				'PUT',
				note
			);
		},
		onError: () => toast.error('Failed to save the note. Please try again.'),
	});
}
